import React, { useCallback, useEffect, useMemo, useState } from "react";

import { CButton, CCard, CCardBody, CPagination } from "@coreui/react";
import {
  FaArrowRight,
  FaCalendarAlt,
  FaCheck,
  FaClipboardList,
  FaUser
} from "react-icons/fa";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import { Row } from "reactstrap";

import Breadcrumb from "../../../components/Breadcrumb";
import FormularioActivityPlayer from "../../../components/Formulario/FormularioActivityPlayer";
import { FormularioEditor } from "../../../components/Formulario/FormularioPicker";
import Loading from "../../../components/Loading";
import ModalActivity from "../../../components/Modal/ModalActivity";
import ProtectedRoute from "../../../components/ProtectedRoute";
import StatisticsFormulario from "../../../components/Statistics/StatisticsFormulario/statistics";
import DataTable from "../../../components/Tables/tables";
import URL from "../../../config/api";
import axios from "../../../config/axios";
import { isGestorDeFormularios } from "../../../config/formularios";
import "../../../components/Formulario/formulario.css";

const auth = token => ({ headers: { authorization: `Bearer ${token}` } });

/* Cada pessoa responde um formulário uma vez só. O `assigned` já diz o que foi
   enviado (`respondido`), e o card fica como "Respondido", sem o botão. Quem
   saiu no meio continua com "Continuar": o backend retoma de onde parou.

   Qualquer perfil pode receber formulário (agendamento por público), então
   esta lista aparece para todos; o gestor a vê numa aba ao lado da gestão. */
function StudentFormularios({ token, assigned, onAnswered, embutido = false }) {
  const [playing, setPlaying] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const formatDeadline = value =>
    value
      ? new Intl.DateTimeFormat("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric"
        }).format(new Date(String(value).replace(" ", "T")))
      : null;
  const totalPages = Math.max(1, Math.ceil(assigned.length / pageSize));
  const visibleFormularios = assigned.slice(
    (page - 1) * pageSize,
    page * pageSize
  );
  const pendentes = assigned.filter(item => !item.respondido).length;

  useEffect(() => {
    setPage(currentPage => Math.min(currentPage, totalPages));
  }, [totalPages]);

  if (playing)
    return (
      <FormularioActivityPlayer
        token={token}
        formularioId={playing}
        onClose={() => {
          setPlaying(null);
          onAnswered();
        }}
      />
    );

  return (
    <div className="animated fadeIn formulario-student-page">
      {!embutido && <Breadcrumb currentName="Formulário" />}
      <header className="formulario-student-page__header">
        <div>
          {!embutido && <h1>Formulário</h1>}
          <p>Responda aos formulários enviados para você.</p>
        </div>
        {assigned.length > 0 && (
          <span className="formulario-student-page__count">
            <FaClipboardList aria-hidden="true" />
            {pendentes} para responder
          </span>
        )}
      </header>
      {assigned.length ? (
        <div className="formulario-student-page__grid">
          {visibleFormularios.map(item => (
            <CCard className="formulario-student-card" key={item.id}>
              <CCardBody>
                <div className="formulario-student-card__title-row">
                  <h2>{item.formulario?.titulo}</h2>
                  <span
                    className={`formulario-student-card__status ${
                      item.respondido ? "is-answered" : ""
                    }`}
                  >
                    {item.respondido ? "Respondido" : "Disponível"}
                  </span>
                </div>
                <p className="formulario-student-card__description">
                  {item.formulario?.descricao ||
                    "Formulário enviado para você."}
                </p>
                <div className="formulario-student-card__details">
                  <span>
                    <FaUser aria-hidden="true" />
                    {item.professor?.name || item.professor_name || "Educandus"}
                  </span>
                  {(item.inicio || item.fim) && (
                    <span>
                      <FaCalendarAlt aria-hidden="true" />
                      {item.fim
                        ? `Disponível até ${formatDeadline(item.fim)}`
                        : "Disponível"}
                    </span>
                  )}
                </div>
                {item.respondido ? (
                  <CButton
                    color="secondary"
                    className="formulario-student-card__action"
                    disabled
                  >
                    <FaCheck aria-hidden="true" /> Respondido
                  </CButton>
                ) : (
                  <CButton
                    color="primary"
                    className="formulario-student-card__action"
                    onClick={() => setPlaying(item.form_id)}
                  >
                    Responder <FaArrowRight aria-hidden="true" />
                  </CButton>
                )}
              </CCardBody>
            </CCard>
          ))}
        </div>
      ) : (
        <div className="formulario-student-page__empty">
          <span className="formulario-student-page__empty-icon">
            <FaClipboardList aria-hidden="true" />
          </span>
          <h2>Nenhum formulário disponível</h2>
          <p>Quando um formulário for enviado para você, ele aparecerá aqui.</p>
        </div>
      )}
      {totalPages > 1 && (
        <div className="formulario-student-page__pagination">
          <CPagination
            activePage={page}
            pages={totalPages}
            onActivePageChange={setPage}
          />
        </div>
      )}
    </div>
  );
}

function FormularioConteudo({ user, general, history }) {
  const token = user.token;
  // Adm Educandus e professor gerenciam; os demais (aluno) respondem
  const isTeacher = isGestorDeFormularios(
    general.active_roles?.role_id ?? user.user_roles?.[0]?.role_id
  );
  const [list, setList] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [reporting, setReporting] = useState(null);
  const [aba, setAba] = useState("gestao");

  const load = useCallback(
    ({ silencioso = false } = {}) => {
      if (!silencioso) setLoading(true);
      // O que foi enviado para a pessoa vem sempre; a gestão, só para gestores
      const pedidos = [
        axios.get(`${URL.api}formulario/assigned`, auth(token)),
        isTeacher ? axios.get(`${URL.api}formulario/list`, auth(token)) : null
      ];
      Promise.all(pedidos)
        .then(([respostaAssigned, respostaList]) => {
          setAssigned(respostaAssigned.data?.data || []);
          if (respostaList) setList(respostaList.data?.data || []);
        })
        .catch(error =>
          toastr.error(
            "Erro",
            error.response?.data?.error ||
              "Não foi possível carregar os formulários."
          )
        )
        .finally(() => setLoading(false));
    },
    [isTeacher, token]
  );

  const pendentesParaMim = assigned.filter(item => !item.respondido).length;

  useEffect(() => load(), [load]);

  const tableList = useMemo(
    () =>
      list.map(formulario => ({
        id: formulario.id,
        nome: formulario.titulo,
        descrição: formulario.descricao || "Sem descrição",
        perguntas: formulario.perguntas?.length || 0,
        formulario
      })),
    [list]
  );

  const deleteFormularios = async ids => {
    try {
      await Promise.all(
        ids.map(id => axios.delete(`${URL.api}formulario/${id}`, auth(token)))
      );
      setList(old => old.filter(formulario => !ids.includes(formulario.id)));
      toastr.success("Sucesso", "Formulário excluído.");
    } catch (error) {
      toastr.error(
        "Erro",
        error.response?.data?.error || "Não foi possível excluir o formulário."
      );
      load();
    }
  };

  const duplicateFormulario = async row => {
    try {
      const response = await axios.post(
        `${URL.api}formulario/${row.id}/duplicate`,
        {},
        auth(token)
      );
      setList(old => [response.data.data, ...old]);
      toastr.success("Sucesso", "Formulário duplicado.");
    } catch (error) {
      toastr.error(
        "Erro",
        error.response?.data?.error || "Não foi possível duplicar o formulário."
      );
    }
  };

  if (loading) return <Loading top="45" left="50" />;
  if (!isTeacher)
    return (
      <StudentFormularios
        token={token}
        assigned={assigned}
        onAnswered={() => load({ silencioso: true })}
      />
    );
  return (
    <>
      {/* Fora do `animated fadeIn`, como o relatório da atividade em
          /activity/list: a animação é do conteúdo da página, e o modal não
          participa dela. */}
      {reporting && (
        <StatisticsFormulario
          show
          formularioId={reporting.id}
          onClose={() => setReporting(null)}
        />
      )}
      <div className="animated fadeIn">
        <Row>
          <Breadcrumb currentName="Listagem de Formulários" />
          {assigned.length > 0 && (
            <nav className="formulario-abas" aria-label="Formulários">
              <button
                type="button"
                className={aba === "gestao" ? "is-active" : ""}
                onClick={() => setAba("gestao")}
              >
                Meus formulários
              </button>
              <button
                type="button"
                className={aba === "responder" ? "is-active" : ""}
                onClick={() => setAba("responder")}
              >
                Para responder
                {pendentesParaMim > 0 && <span>{pendentesParaMim}</span>}
              </button>
            </nav>
          )}
          {aba === "responder" && assigned.length > 0 ? (
            <div className="w-100">
              <StudentFormularios
                embutido
                token={token}
                assigned={assigned}
                onAnswered={() => load({ silencioso: true })}
              />
            </div>
          ) : (
            <div id="list">
              {/* O DataTable da develop aceita só texto no tooltip de editar;
                o modal decide sozinho entre editar e visualizar pelo
                `pode_editar` do formulário. */}
              <DataTable
                responsiveCards
                list={tableList}
                path="/formulario/"
                title="Formulários"
                subtitle="Crie, edite e agende os formulários desta tabela"
                header={[
                  { name: "id", denomination: "ID" },
                  { name: "nome", denomination: "Nome" },
                  { name: "descrição", denomination: "Descrição" },
                  { name: "perguntas", denomination: "Perguntas", width: "110" }
                ]}
                insert
                delete
                action
                actionWidth="230"
                btnEdit
                btnDuplication
                btnAllocate
                btnReport
                btnEditToolTip="Editar ou visualizar formulário"
                btnDuplicationToolTip="Duplicar formulário"
                btnAllocateToolTip="Agendar formulário"
                btnReportToolTip="Respostas do formulário"
                insertAction={() => setEditing(false)}
                editAction={row => setEditing(row.formulario)}
                duplicateAction={duplicateFormulario}
                allocateAction={row =>
                  history.push(`/formulario/allocate/${row.id}`)
                }
                reportAction={row => setReporting(row.formulario)}
                deleteAction={deleteFormularios}
              />
            </div>
          )}
        </Row>
        <ModalActivity
          modalState={editing !== null}
          toggleModal={() => setEditing(null)}
          title={
            editing && editing.id
              ? editing.pode_editar === false
                ? "Visualizar formulário"
                : "Editar formulário"
              : "Montar formulário"
          }
          backdrop="static"
          size="xl"
          className="educandus modalActivity formulario-builder-modal"
          noFooter
        >
          {editing !== null && (
            <FormularioEditor
              formulario={editing || null}
              token={token}
              readOnly={editing?.pode_editar === false}
              onCancel={() => setEditing(null)}
              onSaved={formulario => {
                setList(old => [
                  formulario,
                  ...old.filter(item => item.id !== formulario.id)
                ]);
                setEditing(null);
              }}
            />
          )}
        </ModalActivity>
      </div>
    </>
  );
}

// Quem abre a tela é o access_roles_links do papel (link /formulario),
// conferido pelo ProtectedRoute. Fica fora do FormularioConteudo porque ele tem
// vários returns (carregando, aluno, gestor) e a checagem vale para todos.
function FormularioPage(props) {
  const { currentRoute } = props;
  return (
    <>
      <ProtectedRoute routeId={currentRoute} />
      <FormularioConteudo {...props} />
    </>
  );
}

export default connect(state => ({
  user: state.user,
  general: state.general
}))(FormularioPage);
